import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useConfirm } from 'primevue/useconfirm';
import { useAuth } from '@/composables/useAuth';
import {
  shellNavSections,
  getShellTabByKey,
  getShellAdminTabByKey
} from '../data/shellConfig';
import {
  buildShellAuthContext,
  canSeeShellTab,
  canSeeShellAdminTab
} from '../utils/shellTabVisibility';
import {
  formatUserDisplayName,
  getInitials,
  resolvePrimaryRoleLabel
} from '../utils/sidebarUserUtils';
import { useGlobalSettings } from '@/features/settings/composables/useGlobalSettings';

const toPrimeIconClass = (icon) => {
  if (!icon) return '';
  return icon.startsWith('pi-') ? icon : `pi-${icon}`;
};

function resolveBadgeCount(counts, badgeCountKey) {
  if (!badgeCountKey || !counts || typeof counts !== 'object') return null;
  const value = counts[badgeCountKey];
  if (value === undefined || value === null || value === '') return null;
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  return num;
}

function buildNavItem(tab, counts = {}) {
  // Placeholder modules are not implemented yet — show "Soon", not live counts.
  const count = tab.isPlaceholder ? null : resolveBadgeCount(counts, tab.badgeCountKey);
  const showSoon = !!tab.isPlaceholder;
  return {
    key: tab.key,
    label: tab.label,
    icon: toPrimeIconClass(tab.icon),
    path: tab.path,
    routeName: tab.routeName,
    relatedRouteNames: tab.relatedRouteNames,
    // Placeholders stay navigable (ModulePlaceholderView); not disabled.
    isDisabled: false,
    statusBadge: showSoon ? 'Soon' : null,
    badgeKind: showSoon ? 'status' : count !== null ? 'count' : null,
    badge: count,
    showAlertDot: false,
    ariaLabel: tab.label,
    railTooltip: tab.label
  };
}

function resolveSectionItem(item, ctx, counts = {}) {
  if (item.type === 'tab') {
    const tab = getShellTabByKey(item.tabKey);
    if (!tab || tab.hidden || !canSeeShellTab(tab, ctx)) return null;
    return buildNavItem(tab, counts);
  }

  if (item.type === 'adminTab') {
    const tab = getShellAdminTabByKey(item.tabKey);
    if (!tab || tab.hidden || !canSeeShellAdminTab(tab, ctx)) return null;
    return {
      key: tab.key,
      label: tab.label,
      icon: toPrimeIconClass(tab.icon),
      path: tab.path,
      routeName: null,
      relatedRouteNames: null,
      isDisabled: false,
      statusBadge: null,
      badgeKind: null,
      badge: null,
      showAlertDot: false,
      ariaLabel: tab.label,
      railTooltip: tab.label
    };
  }

  return null;
}

export function useSidebarNav() {
  const router = useRouter();
  const route = useRoute();
  const store = useStore();
  const confirm = useConfirm();
  const auth = useAuth();

  const user = computed(() => store.state.users.user);
  const isLogged = computed(() => !!store.state.users?.user?.isLogged);
  const isMobileSidebar = computed(() => store.state.modals.sidebar.isMobile);

  const authContext = computed(() =>
    buildShellAuthContext({
      ...auth,
      allowedTabs: store.state.app?.allowedTabs || []
    })
  );

  const bootstrapCounts = computed(() => store.state.app?.bootstrap?.counts || {});

  const navSectionGroups = computed(() => {
    const ctx = authContext.value;
    const counts = bootstrapCounts.value;
    const groups = [];

    for (const section of shellNavSections) {
      const items = [];
      for (const itemRef of section.items || []) {
        const navItem = resolveSectionItem(itemRef, ctx, counts);
        if (navItem) items.push(navItem);
      }
      if (items.length === 0) continue;
      groups.push({ id: section.id, label: section.label, items });
    }

    return groups;
  });

  const userCard = computed(() => {
    const displayName = formatUserDisplayName(user.value?.name, user.value?.email);
    const subtitle =
      resolvePrimaryRoleLabel(user.value?.roles) || user.value?.email || 'Signed in';

    return {
      name: displayName,
      subtitle,
      initials: getInitials(displayName)
    };
  });

  const { open: openSettingsModal } = useGlobalSettings();

  const openSettings = (sectionKey = null) => {
    if (!isLogged.value) return;
    openSettingsModal(sectionKey);
  };

  const goToMyProfile = () => {
    openSettings('account');
  };

  const accountMenuActions = computed(() => {
    if (!isLogged.value) return [];

    const actions = [
      {
        label: 'Account',
        icon: 'pi pi-user',
        command: () => openSettings('account')
      }
    ];

    const allowedTabs = store.state.app?.allowedTabs || [];
    if (!allowedTabs.length || allowedTabs.includes('history')) {
      actions.push({
        label: 'History',
        icon: 'pi pi-history',
        command: () => openSettings('history')
      });
    }

    if (allowedTabs.includes('archived')) {
      actions.push({
        label: 'Archived',
        icon: 'pi pi-inbox',
        command: () => router.push({ name: 'archived' }).catch(() => {})
      });
    }

    actions.push({
      label: 'Settings',
      icon: 'pi pi-cog',
      command: () => openSettings()
    });

    return actions;
  });

  const showSidebarBehaviorControls = computed(() => !isMobileSidebar.value);

  const logout = () => {
    confirm.require({
      message: 'Are you sure you want to log out?',
      header: 'Log out',
      icon: 'pi pi-sign-out',
      acceptLabel: 'Log out',
      rejectLabel: 'Cancel',
      accept: () => {
        store.commit('app/clearBootstrap');
        store.commit('users/logoutUser');
        router.push('/');
      }
    });
  };

  return {
    navSectionGroups,
    userCard,
    accountMenuActions,
    showSidebarBehaviorControls,
    goToMyProfile,
    openSettings,
    logout
  };
}
